import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Bell,
  Brain,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Flag,
  Globe,
  Lightbulb,
  Link as LinkIcon,
  ListTodo,
  MoreHorizontal,
  PenLine,
  Plus,
  SlidersHorizontal,
  Sparkles,
  Star,
  Tag,
  Trash2,
  X,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { dueLabelOf, dueDateText } from "../../../lib/dueDates";
import { useDirection } from "../../../hooks/useDirection";
import useRetryFetch from "../../../hooks/useRetryFetch";
import useProcess from "../../../hooks/useProcess";
import { useSpaces } from "../../../context/SpaceProvider";
import {
  createTakeaway,
  deleteTakeaway,
  editTakeaway,
  listTakeaways,
  toggleStatus,
  updateTakeawaySpaces,
  type UserTakeaway,
} from "../../../api/userTakeaways";
import { listCategories, type UserCategory } from "../../../api/userCategories";
import { toggleInsightFavorite } from "../../../api/insightAI";
import {
  cancelReminder,
  getMyReminders,
  type CheckpointReminder,
} from "../../../api/checkpointReminders";
import { listObjects, type MmObject } from "../../../api/mentalModels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import MmLiftMenuItem from "../../mentalModel/MmLiftMenuItem";
import MmModelPage, { type MmInitialVisit } from "../../mentalModel/MmModelPage";
import MmRow from "../../mentalModel/MmRow";
import UnderneathChips from "../../mentalModel/UnderneathChips";
import { useUnderneath } from "../../../hooks/useUnderneath";
import { getStoredRegister, storeRegister, useMmText } from "../../../hooks/useMmText";
import MmListSections from "../../mentalModel/MmListSections";
import ActionItemDialog from "./actionItems/ActionItemDialog";
import BreakdownDialog from "./actionItems/BreakdownDialog";
import ActionToActivityDialog from "./actionItems/ActionToActivityDialog";
import NeedHelpDialog from "./actionItems/NeedHelpDialog";
import ProgressReportDialog from "./actionItems/ProgressReportDialog";
import InsightDialog from "./insights/InsightDialog";
import InsightToActionDialog from "./insights/InsightToActionDialog";
import AIExtractorDialog from "./insights/AIExtractorDialog";
import type { AIExtractedAction, AIExtractedInsight } from "./insights/types";
import {
  DueChip,
  invalidateWorkspace,
  relTime,
  SpaceBadge,
  SpaceMiniChips,
  spacesOf,
} from "./WorkspaceBoard";
import { useProjectIndex } from "../../../hooks/useProjectIndex";
import { ProjectChip, ProjectLink, projectPath } from "./ProjectChip";

/**
 * Design 3b — the section as a triage surface. The list opens full-width;
 * clicking an item slides in the detail pane, which carries the classic
 * table's complete action set (the full-tool views stay one click away).
 */

export interface WorkspaceCounts {
  pendingActions: number;
  totalInsights: number;
  upcomingReminders: number;
  mentalModels: number;
}

interface FocusProps {
  section: "action_items" | "insights" | "reminders" | "mental_models";
  initialId?: string | null;
  onSwitchToClassic: () => void;
  /** Design 6b — list and detail become two full screens; the zone list turns
   *  into a segmented chips header that switches sections in place. */
  mobile?: boolean;
  counts?: WorkspaceCounts;
  onSwitchSection?: (section: FocusProps["section"]) => void;
  /** Models: a staged open lands the model page on its visit step. */
  mmInitialVisit?: MmInitialVisit;
  /** Models: the page took the staged visit (the host forgets it). */
  onMmInitialVisitConsumed?: () => void;
  /** Models: open the "Your skills & patterns" studio. */
  onOpenStudio?: () => void;
}

const WorkspaceFocus = (props: FocusProps) => {
  switch (props.section) {
    case "action_items":
      return <ActionsFocus {...props} />;
    case "insights":
      return <InsightsFocus {...props} />;
    case "reminders":
      return <RemindersFocus {...props} />;
    case "mental_models":
      return <ModelsFocus {...props} />;
    default:
      return null;
  }
};

// ── Layout shell shared by all four sections ─────────────────────────────────
// Desktop: no selection → the list owns the whole pane; a selection splits it.
// Mobile (6b): list and detail are two screens — tapping an item pushes the
// detail, the back header pops it.

const ZONES = [
  {
    id: "action_items",
    icon: CheckSquare,
    labelKey: "actions",
    pick: (c: WorkspaceCounts) => c.pendingActions,
    activeCls: "bg-primary/10 border-primary/30 text-primary",
  },
  {
    id: "insights",
    icon: Lightbulb,
    labelKey: "insights",
    pick: (c: WorkspaceCounts) => c.totalInsights,
    activeCls: "bg-amber-50 border-amber-300 text-amber-700",
  },
  {
    id: "reminders",
    icon: Bell,
    labelKey: "workspace_pill_reminders",
    pick: (c: WorkspaceCounts) => c.upcomingReminders,
    activeCls: "bg-purple-50 border-purple-300 text-purple-700",
  },
  {
    id: "mental_models",
    icon: Brain,
    labelKey: "workspace_pill_models",
    pick: (c: WorkspaceCounts) => c.mentalModels,
    activeCls: "bg-primary/10 border-primary/30 text-primary",
  },
] as const;

/** 6b's segmented header — the four zones as chips, switching in place. */
const ZoneChips = ({
  section,
  counts,
  onSwitchSection,
}: {
  section: FocusProps["section"];
  counts?: WorkspaceCounts;
  onSwitchSection: (s: FocusProps["section"]) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-3 pb-2.5">
      {ZONES.map((z) => {
        const Icon = z.icon;
        const active = z.id === section;
        const count = counts ? z.pick(counts) : 0;
        return (
          <button
            key={z.id}
            onClick={() => !active && onSwitchSection(z.id)}
            className={cn(
              "flex-none inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap",
              active
                ? cn("font-semibold", z.activeCls)
                : "border-border/70 text-muted-foreground"
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {t(z.labelKey)}
            {count > 0 && <span className="tabular-nums">{count}</span>}
          </button>
        );
      })}
    </div>
  );
};

/** Mobile rows advertise that they push a screen. */
const RowChevron = () => {
  const { isRTL } = useDirection();
  return (
    <ChevronRight
      className={cn(
        "w-4 h-4 self-center flex-none text-muted-foreground/40",
        isRTL && "rotate-180"
      )}
    />
  );
};

const FocusShell = ({
  title,
  headerActions,
  pills,
  list,
  detail,
  onCloseDetail,
  focus,
}: {
  title: string;
  headerActions?: ReactNode;
  pills?: ReactNode;
  list: ReactNode;
  detail: ReactNode | null;
  onCloseDetail: () => void;
  /** The hosting section's props — carries the 6b mobile mode + zone switcher. */
  focus?: FocusProps;
}) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const split = detail != null;

  if (focus?.mobile) {
    if (split) {
      // Detail screen — back header pops to the list
      return (
        <div className="flex flex-col h-full min-h-0 bg-[#fafbfc]">
          <div className="flex-none sticky top-0 z-20 flex items-center gap-1 px-2 py-1.5 bg-card/95 backdrop-blur-sm border-b border-border/50">
            <button
              onClick={onCloseDetail}
              aria-label={t("back")}
              className="w-9 h-9 rounded-lg inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className={cn("w-5 h-5", isRTL && "rotate-180")} />
            </button>
            <span className="text-[13px] text-muted-foreground truncate">{title}</span>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground/60 mb-2.5">
              {t("workspace_detail")}
            </p>
            {detail}
          </div>
        </div>
      );
    }
    // List screen — workspace title, zone chips, status pills, flat list
    return (
      <div className="flex flex-col h-full min-h-0 bg-card">
        <div className="px-3 pt-3 pb-2.5 flex items-center justify-between gap-2">
          <span className="text-lg font-semibold tracking-tight text-foreground truncate">
            {focus.onSwitchSection ? t("my_workspace", "My Workspace") : title}
          </span>
          <div className="flex items-center gap-1.5 flex-none">{headerActions}</div>
        </div>
        {focus.onSwitchSection && (
          <ZoneChips
            section={focus.section}
            counts={focus.counts}
            onSwitchSection={focus.onSwitchSection}
          />
        )}
        {pills && (
          <div className="px-3 pb-2.5 flex gap-1.5 overflow-x-auto no-scrollbar">{pills}</div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto px-1.5 pb-4">
          <div className="space-y-0.5">{list}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      <aside
        className={cn(
          "flex flex-col min-h-0 bg-card min-w-0",
          split ? "w-[390px] max-w-[45%] flex-none border-e border-border/50" : "flex-1"
        )}
      >
        <div className="px-[18px] pt-4 pb-2.5 flex items-center justify-between gap-2">
          <span className="text-[15px] font-semibold text-foreground truncate">{title}</span>
          <div className="flex items-center gap-1.5 flex-none">{headerActions}</div>
        </div>
        {pills && <div className="px-[18px] pb-2.5 flex flex-wrap gap-1.5">{pills}</div>}
        <div className="flex-1 min-h-0 overflow-y-auto px-2.5 pb-3">
          <div className={cn("space-y-0.5", !split && "max-w-[820px]")}>{list}</div>
        </div>
      </aside>
      {split && (
        <section className="flex-1 min-w-0 overflow-y-auto bg-[#fafbfc] px-6 py-5 md:px-[30px]">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground/60">
              {t("workspace_detail")}
            </p>
            <button
              onClick={onCloseDetail}
              aria-label={t("close", "Close")}
              className="w-[26px] h-[26px] rounded-lg text-muted-foreground/60 hover:text-foreground hover:bg-muted/60 inline-flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {detail}
        </section>
      )}
    </div>
  );
};

const HeaderIconButton = ({
  onClick,
  label,
  children,
  primary,
  mobile,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
  primary?: boolean;
  mobile?: boolean;
}) => (
  <button
    onClick={onClick}
    title={label}
    aria-label={label}
    className={cn(
      "inline-flex items-center justify-center transition-colors",
      mobile ? "w-9 h-9 rounded-[11px]" : "w-[26px] h-[26px] rounded-lg",
      primary
        ? "bg-primary text-primary-foreground hover:bg-primary/90"
        : "border border-border/70 text-muted-foreground hover:text-foreground hover:border-border"
    )}
  >
    {children}
  </button>
);

const Pill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "text-[11.5px] px-[11px] py-1 rounded-full border transition-colors whitespace-nowrap",
      active
        ? "bg-primary text-primary-foreground border-primary font-medium"
        : "border-border/70 text-muted-foreground hover:text-foreground"
    )}
  >
    {children}
  </button>
);

const EmptyList = () => {
  const { t } = useTranslation();
  return (
    <p className="text-[12.5px] text-muted-foreground/70 px-3 py-4">
      {t("workspace_empty_list")}
    </p>
  );
};

/** Selection is user-driven: nothing opens until a row is clicked, and a
 *  vanished item (deleted, filtered away by a mutation) closes the pane. */
function useSelection<T extends { _id: string }>(pool: T[], ready: boolean, initialId?: string | null) {
  const [selectedId, setSelectedId] = useState<string | null>(initialId ?? null);
  useEffect(() => {
    if (!ready || !selectedId) return;
    if (!pool.some((i) => i._id === selectedId)) setSelectedId(null);
  }, [pool, ready, selectedId]);
  const selected = pool.find((i) => i._id === selectedId) ?? null;
  return { selected, selectedId, setSelectedId };
}

const primaryBtnCls =
  "inline-flex items-center gap-1.5 rounded-[10px] bg-primary text-primary-foreground px-4 py-2 text-[12.5px] font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50";
const secondaryBtnCls =
  "inline-flex items-center gap-1.5 rounded-[10px] border border-border/70 bg-card px-3.5 py-2 text-[12.5px] font-medium text-foreground/70 hover:text-foreground hover:border-border transition-colors disabled:opacity-50";

const CategoryBadge = ({ category }: { category: NonNullable<UserTakeaway["category"]> }) => (
  <span
    className="text-[11px] px-2.5 py-[3px] rounded-full border"
    style={{
      color: category.color,
      borderColor: `${category.color}55`,
      background: `${category.color}0d`,
    }}
  >
    {category.name}
  </span>
);

// ── Action items ─────────────────────────────────────────────────────────────

type ActionPill = "due" | "resting" | "done";

const ActionsFocus = (props: FocusProps) => {
  const { initialId, onSwitchToClassic, mobile } = props;
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const { startProcess, resumeProcess } = useProcess();
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();
  const [pill, setPill] = useState<ActionPill>("due");
  const [busy, setBusy] = useState(false);

  // 6b stacks the detail's actions for thumbs
  const btnP = cn(primaryBtnCls, mobile && "w-full justify-center py-2.5");
  const btnS = cn(secondaryBtnCls, mobile && "flex-1 justify-center py-2.5");

  // Dialog state — the same dialogs the classic table uses, hosted here
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<UserTakeaway | null>(null);
  const [breakdownFor, setBreakdownFor] = useState<UserTakeaway | null>(null);
  const [activityFor, setActivityFor] = useState<UserTakeaway | null>(null);
  const [needHelpFor, setNeedHelpFor] = useState<UserTakeaway | null>(null);
  const [progressFor, setProgressFor] = useState<UserTakeaway | null>(null);

  // One unfiltered fetch: the pills, the Done list and the sub-task trees are
  // all cuts of the same data.
  const { data, isFetched } = useQuery({
    queryKey: ["workspace", "focus", "actions"],
    queryFn: () => listTakeaways(retryFetch, { type: "action", limit: 300 }),
    staleTime: 30_000,
  });
  const { data: categoriesData } = useQuery({
    queryKey: ["workspace", "categories"],
    queryFn: () => listCategories(retryFetch).catch(() => ({ status: "LIST", list: [] as UserCategory[] })),
    staleTime: 5 * 60_000,
  });
  const categories = ((categoriesData as any)?.list ?? []) as UserCategory[];
  const actionCategories = categories.filter((c) => c.type === "action" || c.type === "both");

  const all = useMemo(() => data?.list ?? [], [data]);
  const topLevel = useMemo(
    () => all.filter((a) => !a.parentId && a.status !== "archived"),
    [all]
  );
  const items = useMemo(() => {
    if (pill === "done")
      return topLevel
        .filter((a) => a.status === "completed")
        .sort((a, b) => (b.completedOn ?? 0) - (a.completedOn ?? 0));
    const pending = topLevel.filter((a) => a.status === "pending");
    if (pill === "due")
      return pending.filter((a) => a.dueDate).sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0));
    return pending
      .filter((a) => !a.dueDate)
      .sort((a, b) => (b.createdOn ?? 0) - (a.createdOn ?? 0));
  }, [topLevel, pill]);

  const { selected, selectedId, setSelectedId } = useSelection(topLevel, isFetched, initialId);
  // One batched lookup for the visible list (+ the open item) — §D chips
  const underneath = useUnderneath([...items.map((i) => i._id), selectedId]);
  const subtasks = useMemo(
    () =>
      selected
        ? all
            .filter((a) => a.parentId === selected._id)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        : [],
    [all, selected]
  );

  const run = async (fn: () => Promise<unknown>, successMsg?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      invalidateWorkspace();
    } catch (e: any) {
      toast.error(e.message ?? "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (item: UserTakeaway) =>
    run(
      () => toggleStatus(retryFetch, { id: item._id }),
      item.status === "pending" ? t("workspace_marked_done") : t("workspace_reopened")
    );
  const reschedule = (item: UserTakeaway, dueDate: number | null) =>
    run(() => editTakeaway(retryFetch, { id: item._id, dueDate }), t("workspace_rescheduled"));
  const setPriority = (item: UserTakeaway, priority: "high" | "medium" | "low" | null) =>
    run(() => editTakeaway(retryFetch, { id: item._id, priority }));
  const setCategory = (item: UserTakeaway, category: string | null) =>
    run(() => editTakeaway(retryFetch, { id: item._id, category }));
  const setSpaces = (item: UserTakeaway, spaceIds: string[]) =>
    run(() => updateTakeawaySpaces(retryFetch, item._id, spaceIds));
  const removeItem = (item: UserTakeaway) =>
    run(async () => {
      await deleteTakeaway(retryFetch, { id: item._id });
      setSelectedId(null);
    }, t("workspace_deleted"));

  const openActivity = (item: UserTakeaway) => {
    const linked = item.linkedActivity;
    if (!linked?.prescriptionId) return;
    const lastEngagement = linked.engagementProgress?.lastEngagementId;
    if (lastEngagement) {
      resumeProcess({ processId: lastEngagement, prescriptionId: linked.prescriptionId });
    } else {
      startProcess({ prescriptionId: linked.prescriptionId });
    }
  };

  const at = (h: number, addDays: number) => {
    const d = new Date();
    d.setDate(d.getDate() + addDays);
    d.setHours(h, 0, 0, 0);
    return d.getTime();
  };

  const saveDialog = async (data: {
    content: string;
    priority?: "high" | "medium" | "low";
    category?: string;
    dueDate?: Date;
    spaceIds?: string[];
    isLongTermGoal?: boolean;
  }) => {
    if (editItem) {
      await editTakeaway(retryFetch, {
        id: editItem._id,
        content: data.content,
        priority: data.priority ?? null,
        category: data.category ?? null,
        dueDate: data.dueDate ? data.dueDate.getTime() : null,
        spaceIds: data.spaceIds,
        isLongTermGoal: data.isLongTermGoal,
      });
      toast.success(t("action_item_updated", "Action item updated"));
    } else {
      const response = await createTakeaway(retryFetch, {
        type: "action",
        content: data.content,
        priority: data.priority || undefined,
        category: data.category || undefined,
        dueDate: data.dueDate ? data.dueDate.getTime() : undefined,
        spaceIds: data.spaceIds,
        isLongTermGoal: data.isLongTermGoal,
        source: { type: "user_created" },
      });
      if (response.status !== "CREATED") throw new Error(response.fault ?? "Could not save");
      toast.success(t("action_item_created"));
    }
    invalidateWorkspace();
  };

  const dueBadge = (item: UserTakeaway) => {
    if (!item.dueDate) return null;
    const label = dueLabelOf(item.dueDate, Date.now());
    if (label.kind === "today")
      return (
        <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-red-50 border border-red-200 text-red-600">
          {t("workspace_due_today_badge")}
        </span>
      );
    if (label.kind === "carried_weekday" || label.kind === "carried_since")
      return (
        <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-amber-50 border border-amber-200 text-amber-700">
          {label.kind === "carried_weekday"
            ? t("workspace_carried_from", { day: dueDateText(label, i18n.language) })
            : t("workspace_carried_since", { date: dueDateText(label, i18n.language) })}
        </span>
      );
    return (
      <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-card border border-border/70 text-muted-foreground">
        {label.date.toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" })}
      </span>
    );
  };

  const priorityBadge = (item: UserTakeaway) =>
    item.priority ? (
      <span
        className={cn(
          "text-[11px] px-2.5 py-[3px] rounded-full border inline-flex items-center gap-1",
          item.priority === "high" && "bg-red-50 border-red-200 text-red-600",
          item.priority === "medium" && "bg-orange-50 border-orange-200 text-orange-600",
          item.priority === "low" && "bg-primary/5 border-primary/20 text-primary"
        )}
      >
        <Flag className="w-2.5 h-2.5" />
        {t(item.priority)}
      </span>
    ) : null;

  const rowSub = (item: UserTakeaway) => {
    if (pill === "done" && item.completedOn)
      return (
        <span className="text-[11px] text-emerald-700/80">
          {t("workspace_done_ago", { time: relTime(item.completedOn, i18n.language) })}
        </span>
      );
    if (!item.dueDate)
      return <span className="text-[11px] text-muted-foreground/60">{t("workspace_no_date")}</span>;
    const label = dueLabelOf(item.dueDate, Date.now());
    if (label.kind === "today")
      return <span className="text-[11px] text-red-600">{t("workspace_due_today_badge")}</span>;
    if (label.kind === "carried_weekday")
      return (
        <span className="text-[11px] text-amber-700">
          {t("workspace_carried_from", { day: dueDateText(label, i18n.language) })}
        </span>
      );
    if (label.kind === "carried_since")
      return (
        <span className="text-[11px] text-amber-700">
          {t("workspace_carried_since", { date: dueDateText(label, i18n.language) })}
        </span>
      );
    return (
      <span className="text-[11px] text-muted-foreground/70">{dueDateText(label, i18n.language)}</span>
    );
  };

  return (
    <>
      <FocusShell
        title={t("action_items")}
        focus={props}
        onCloseDetail={() => setSelectedId(null)}
        headerActions={
          <>
            <HeaderIconButton mobile={mobile} onClick={onSwitchToClassic} label={t("workspace_view_full")}>
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </HeaderIconButton>
            <HeaderIconButton
              primary
              mobile={mobile}
              onClick={() => {
                setEditItem(null);
                setAddOpen(true);
              }}
              label={t("add_action_item")}
            >
              <Plus className={mobile ? "w-5 h-5" : "w-3.5 h-3.5"} />
            </HeaderIconButton>
          </>
        }
        pills={
          <>
            <Pill active={pill === "due"} onClick={() => setPill("due")}>
              {t("workspace_filter_due")}
            </Pill>
            <Pill active={pill === "resting"} onClick={() => setPill("resting")}>
              {t("workspace_filter_resting")}
            </Pill>
            <Pill active={pill === "done"} onClick={() => setPill("done")}>
              {t("workspace_filter_done")}
            </Pill>
          </>
        }
        list={
          items.length === 0 ? (
            <EmptyList />
          ) : (
            items.map((item) => (
              <div
                key={item._id}
                className={cn(
                  "flex gap-2.5 px-2.5 py-[11px] rounded-xl cursor-pointer transition-colors",
                  item._id === selectedId
                    ? "bg-primary/[0.06] border border-primary/15"
                    : "border border-transparent hover:bg-muted/40"
                )}
                onClick={() => setSelectedId(item._id === selectedId ? null : item._id)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(item);
                  }}
                  disabled={busy}
                  aria-label={
                    item.status === "pending" ? t("workspace_mark_done") : t("workspace_reopen")
                  }
                  className={cn(
                    "w-[17px] h-[17px] rounded-full border-[1.5px] flex-none mt-0.5 inline-flex items-center justify-center transition-colors disabled:opacity-50",
                    item.status === "completed"
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : "border-gray-300 hover:border-primary"
                  )}
                >
                  {item.status === "completed" && <Check className="w-2.5 h-2.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      "text-[13px] leading-snug line-clamp-2",
                      item._id === selectedId ? "font-medium text-foreground" : "text-foreground/85",
                      item.status === "completed" && "line-through text-muted-foreground"
                    )}
                  >
                    {item.content}
                  </p>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    {rowSub(item)}
                    {item.subtaskProgress && item.subtaskProgress.total > 0 && (
                      <span className="text-[11px] text-muted-foreground/60 inline-flex items-center gap-1">
                        <ListTodo className="w-3 h-3" />
                        {item.subtaskProgress.completed}/{item.subtaskProgress.total}
                      </span>
                    )}
                    {item.category && (
                      <span
                        className="text-[10.5px] px-1.5 py-px rounded-full"
                        style={{ color: item.category.color, background: `${item.category.color}14` }}
                      >
                        {item.category.name}
                      </span>
                    )}
                    <SpaceMiniChips item={item} spaces={spaces} />
                    <ProjectChip item={item} projectById={projectById} spaces={spaces} />
                  </div>
                </div>
                <DueChip item={item} />
                {mobile && <RowChevron />}
              </div>
            ))
          )
        }
        detail={
          selected && (
            <div className="max-w-[640px]">
              <h3 className="text-lg font-semibold tracking-tight text-foreground mb-1.5 whitespace-pre-wrap">
                {selected.content}
              </h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {dueBadge(selected)}
                {priorityBadge(selected)}
                <ProjectLink item={selected} projectById={projectById} onOpen={(p) => navigate(projectPath(p))} />
                {selected.source?.activityTitle && (
                  <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-primary/5 border border-primary/20 text-primary">
                    {t("workspace_from_source", { name: selected.source.activityTitle })}
                  </span>
                )}
                {selected.category && <CategoryBadge category={selected.category} />}
                {spacesOf(selected, spaces).map((s) => (
                  <SpaceBadge key={s._id} space={s} />
                ))}
                {selected.isLongTermGoal && (
                  <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-violet-50 border border-violet-200 text-violet-700">
                    {t("long_term_goal", "Long-term goal")}
                  </span>
                )}
              </div>
              <UnderneathChips entityId={selected._id} byId={underneath} className="-mt-2 mb-4" />

              {/* Primary acts + the classic table's full menu */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => toggle(selected)} disabled={busy} className={btnP}>
                  <Check className="w-3.5 h-3.5" />
                  {selected.status === "pending"
                    ? t("workspace_mark_done")
                    : t("workspace_reopen")}
                </button>
                {selected.status === "pending" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button disabled={busy} className={btnS}>
                        <Clock className="w-3.5 h-3.5" />
                        {t("workspace_later")}
                        <ChevronDown className="w-3 h-3 opacity-60" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => reschedule(selected, at(9, 1))}>
                        {t("workspace_later_tomorrow")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => reschedule(selected, at(9, 7))}>
                        {t("workspace_later_next_week")}
                      </DropdownMenuItem>
                      {selected.dueDate && (
                        <DropdownMenuItem onClick={() => reschedule(selected, null)}>
                          {t("workspace_later_clear")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <button
                  onClick={() => setNeedHelpFor(selected)}
                  className={btnS}
                  title={t("action_items.need_help", "Need help?")}
                >
                  <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                  {t("action_items.need_help", "Need help?")}
                </button>
                <button
                  onClick={() => {
                    setEditItem(selected);
                    setAddOpen(true);
                  }}
                  className={btnS}
                >
                  <PenLine className="w-3.5 h-3.5" />
                  {t("edit")}
                </button>
                {selected.linkedActivity?.prescriptionId && (
                  <button onClick={() => openActivity(selected)} className={btnS}>
                    {t("workspace_open_activity")}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={btnS} aria-label={t("more", "More")}>
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Flag className="h-4 w-4 me-2" />
                        {t("set_priority")}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem onClick={() => setPriority(selected, null)}>
                            <X className="h-4 w-4 me-2 text-muted-foreground" />
                            {t("no_priority")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setPriority(selected, "high")}>
                            <Flag className="h-4 w-4 me-2 text-red-500" />
                            {t("high")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setPriority(selected, "medium")}>
                            <Flag className="h-4 w-4 me-2 text-orange-500" />
                            {t("medium")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setPriority(selected, "low")}>
                            <Flag className="h-4 w-4 me-2 text-primary" />
                            {t("low")}
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Tag className="h-4 w-4 me-2" />
                        {t("assign_category")}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem onClick={() => setCategory(selected, null)}>
                            <X className="h-4 w-4 me-2 text-muted-foreground" />
                            {t("no_category")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {actionCategories.map((category) => (
                            <DropdownMenuItem
                              key={category._id}
                              onClick={() => setCategory(selected, category._id)}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full border"
                                  style={{ backgroundColor: category.color }}
                                />
                                {category.name}
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    {spaces && spaces.length > 0 && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <Globe className="h-4 w-4 me-2" />
                          {t("assign_to_space", "Assign to Space")}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuSubContent>
                            {spaces.map((space: any) => {
                              const isAssigned = selected.spaceIds?.includes(space._id);
                              return (
                                <DropdownMenuItem
                                  key={space._id}
                                  onClick={() => {
                                    const currentIds = selected.spaceIds || [];
                                    setSpaces(
                                      selected,
                                      isAssigned
                                        ? currentIds.filter((id) => id !== space._id)
                                        : [...currentIds, space._id]
                                    );
                                  }}
                                >
                                  <div className="flex items-center gap-2 w-full">
                                    <div
                                      className="w-3 h-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: space.color }}
                                    />
                                    <span className="flex-1 truncate">{space.name}</span>
                                    {isAssigned && (
                                      <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                    )}
                                  </div>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                      </DropdownMenuSub>
                    )}
                    <MmLiftMenuItem entityId={selected._id} />
                    {selected.status !== "completed" && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setBreakdownFor(selected)}>
                          <ListTodo className="h-4 w-4 me-2 text-primary" />
                          {t("action_items.add_subtasks", "Add Sub-tasks")}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onClick={() => setActivityFor(selected)}>
                      <Sparkles className="h-4 w-4 me-2 text-violet-500" />
                      {t("action_items.create_activity", "Create Activity")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setProgressFor(selected)}>
                      <FileText className="h-4 w-4 me-2 text-teal-500" />
                      {t("progress_report", "Progress Update")}
                    </DropdownMenuItem>
                    {selected.source.type === "engagement" && selected.source.fiProcess && (
                      <DropdownMenuItem
                        onClick={() => navigate(`/review?pid=${selected.source.fiProcess}`)}
                      >
                        <LinkIcon className="h-4 w-4 me-2" />
                        {t("view_engagement")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => removeItem(selected)}
                    >
                      <Trash2 className="h-4 w-4 me-2" />
                      {t("delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Provenance */}
              <div className="bg-card border border-border/60 rounded-[14px] px-[18px] py-4 text-[13px] leading-relaxed text-muted-foreground mb-3.5 space-y-2">
                <p>
                  {selected.source?.activityTitle
                    ? t("workspace_ctx_from_activity", {
                        name: selected.source.activityTitle,
                        time: relTime(selected.createdOn, i18n.language),
                      })
                    : t("workspace_ctx_created", {
                        time: relTime(selected.createdOn, i18n.language),
                      })}
                </p>
                {selected.latestProgressReport && (
                  <p className="italic">"{selected.latestProgressReport.content}"</p>
                )}
              </div>

              {/* Sub-tasks — live rows, not just a count */}
              {(subtasks.length > 0 || (selected.subtaskProgress?.total ?? 0) > 0) && (
                <div className="bg-card border border-border/60 rounded-[14px] px-[18px] py-4 mb-3.5">
                  <div className="flex items-center justify-between mb-2.5">
                    <p className="text-[12px] font-semibold text-foreground inline-flex items-center gap-1.5">
                      <ListTodo className="w-3.5 h-3.5 text-primary" />
                      {t("workspace_subtasks")}
                    </p>
                    {selected.subtaskProgress && selected.subtaskProgress.total > 0 && (
                      <span className="text-[11px] text-muted-foreground">
                        {t("workspace_steps_done", {
                          completed: selected.subtaskProgress.completed,
                          total: selected.subtaskProgress.total,
                        })}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {subtasks.map((st) => (
                      <div key={st._id} className="flex items-start gap-2.5 py-1">
                        <button
                          onClick={() => toggle(st)}
                          disabled={busy}
                          className={cn(
                            "w-[15px] h-[15px] rounded-full border-[1.5px] flex-none mt-0.5 inline-flex items-center justify-center transition-colors disabled:opacity-50",
                            st.status === "completed"
                              ? "bg-emerald-500 border-emerald-500 text-white"
                              : "border-gray-300 hover:border-primary"
                          )}
                        >
                          {st.status === "completed" && <Check className="w-2 h-2" />}
                        </button>
                        <span
                          className={cn(
                            "text-[12.5px] leading-snug text-foreground/85",
                            st.status === "completed" && "line-through text-muted-foreground"
                          )}
                        >
                          {st.content}
                        </span>
                      </div>
                    ))}
                  </div>
                  {selected.status !== "completed" && (
                    <button
                      onClick={() => setBreakdownFor(selected)}
                      className="mt-2 text-[11.5px] text-primary hover:underline underline-offset-2 inline-flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      {t("action_items.add_subtasks", "Add Sub-tasks")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        }
      />

      <ActionItemDialog
        open={addOpen}
        onOpenChange={(o) => {
          setAddOpen(o);
          if (!o) setEditItem(null);
        }}
        onSave={saveDialog}
        categories={actionCategories}
        item={editItem ?? undefined}
      />
      <BreakdownDialog
        open={!!breakdownFor}
        onOpenChange={(o) => !o && setBreakdownFor(null)}
        actionItem={breakdownFor}
        onSubtasksCreated={() => {
          setBreakdownFor(null);
          invalidateWorkspace();
        }}
      />
      <ActionToActivityDialog
        open={!!activityFor}
        onOpenChange={(o) => !o && setActivityFor(null)}
        actionItem={activityFor}
        onActivityCreated={() => {
          setActivityFor(null);
          invalidateWorkspace();
        }}
      />
      <NeedHelpDialog
        open={!!needHelpFor}
        onOpenChange={(o) => !o && setNeedHelpFor(null)}
        actionItem={needHelpFor}
        onBreakDown={() => {
          setBreakdownFor(needHelpFor);
          setNeedHelpFor(null);
        }}
        onCreateActivity={() => {
          setActivityFor(needHelpFor);
          setNeedHelpFor(null);
        }}
      />
      {progressFor && (
        <ProgressReportDialog
          open={!!progressFor}
          onOpenChange={(o) => !o && setProgressFor(null)}
          takeawayId={progressFor._id}
          takeawayContent={progressFor.content}
        />
      )}
    </>
  );
};

// ── Insights ─────────────────────────────────────────────────────────────────

const InsightsFocus = (props: FocusProps) => {
  const { initialId, onSwitchToClassic, mobile } = props;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();
  const btnP = cn(primaryBtnCls, mobile && "w-full justify-center py-2.5");
  const btnS = cn(secondaryBtnCls, mobile && "flex-1 justify-center py-2.5");
  const [pill, setPill] = useState<"recent" | "archived">("recent");
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<UserTakeaway | null>(null);
  const [toActionFor, setToActionFor] = useState<UserTakeaway | null>(null);
  const [extractOpen, setExtractOpen] = useState(false);

  const { data, isFetched } = useQuery({
    queryKey: ["workspace", "focus", "insights", pill],
    queryFn: () =>
      listTakeaways(retryFetch, {
        type: "insight",
        status: pill === "archived" ? "archived" : "pending",
        limit: 100,
      }),
    staleTime: 30_000,
  });
  const { data: categoriesData } = useQuery({
    queryKey: ["workspace", "categories"],
    queryFn: () => listCategories(retryFetch).catch(() => ({ status: "LIST", list: [] as UserCategory[] })),
    staleTime: 5 * 60_000,
  });
  const categories = ((categoriesData as any)?.list ?? []) as UserCategory[];
  const insightCategories = categories.filter((c) => c.type === "insight" || c.type === "both");

  const items = useMemo(
    () => [...(data?.list ?? [])].sort((a, b) => (b.createdOn ?? 0) - (a.createdOn ?? 0)),
    [data]
  );
  const { selected, selectedId, setSelectedId } = useSelection(items, isFetched, initialId);
  const underneath = useUnderneath(items.map((i) => i._id));

  const run = async (fn: () => Promise<unknown>, successMsg?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      invalidateWorkspace();
    } catch (e: any) {
      toast.error(e.message ?? "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const setArchived = (item: UserTakeaway, archived: boolean) =>
    run(
      () => editTakeaway(retryFetch, { id: item._id, status: archived ? "archived" : "pending" }),
      archived ? t("workspace_archived") : t("workspace_unarchived")
    );
  const setCategory = (item: UserTakeaway, category: string | null) =>
    run(() => editTakeaway(retryFetch, { id: item._id, category }));
  const setSpaces = (item: UserTakeaway, spaceIds: string[]) =>
    run(() => updateTakeawaySpaces(retryFetch, item._id, spaceIds));
  const toggleFavorite = (item: UserTakeaway) =>
    run(() => toggleInsightFavorite(retryFetch, item._id));
  const removeItem = (item: UserTakeaway) =>
    run(async () => {
      await deleteTakeaway(retryFetch, { id: item._id });
      setSelectedId(null);
    }, t("workspace_deleted"));

  const createActionsFromInsight = async (
    actions: Array<{ content: string; priority?: "high" | "medium" | "low"; category?: string; dueDate?: number }>
  ) => {
    for (const a of actions) {
      await createTakeaway(retryFetch, {
        type: "action",
        content: a.content,
        priority: a.priority,
        category: a.category,
        dueDate: a.dueDate,
        source: { type: "user_created" },
      });
    }
    toast.success(t("workspace_action_from_insight"));
    invalidateWorkspace();
  };

  const handleExtractComplete = async (
    extractedInsights: AIExtractedInsight[],
    extractedActions?: AIExtractedAction[]
  ) => {
    const byName = new Map(categories.map((c) => [c.name.toLowerCase(), c._id]));
    try {
      for (const ins of extractedInsights) {
        await createTakeaway(retryFetch, {
          type: "insight",
          content: ins.content,
          category: ins.suggestedCategory
            ? byName.get(ins.suggestedCategory.toLowerCase())
            : undefined,
          source: { type: "user_created" },
        });
      }
      for (const act of extractedActions ?? []) {
        await createTakeaway(retryFetch, {
          type: "action",
          content: act.content,
          priority: act.suggestedPriority,
          dueDate: act.suggestedDueDate,
          category: act.suggestedCategory
            ? byName.get(act.suggestedCategory.toLowerCase())
            : undefined,
          source: { type: "user_created" },
        });
      }
      toast.success(
        t("workspace_extracted_created", {
          count: extractedInsights.length + (extractedActions?.length ?? 0),
        })
      );
      invalidateWorkspace();
    } catch (e: any) {
      toast.error(e.message ?? "Could not save extracted items");
    }
  };

  const isFav = (item: UserTakeaway) => Boolean((item as any).isFavorite);

  return (
    <>
      <FocusShell
        title={t("insights")}
        focus={props}
        onCloseDetail={() => setSelectedId(null)}
        headerActions={
          <>
            <HeaderIconButton mobile={mobile} onClick={onSwitchToClassic} label={t("workspace_view_full")}>
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </HeaderIconButton>
            <HeaderIconButton
              mobile={mobile}
              onClick={() => setExtractOpen(true)}
              label={t("ai_extract", "Extract from text")}
            >
              <Sparkles className="w-3.5 h-3.5 text-violet-500" />
            </HeaderIconButton>
            <HeaderIconButton
              primary
              mobile={mobile}
              onClick={() => {
                setEditItem(null);
                setAddOpen(true);
              }}
              label={t("log_insight")}
            >
              <Plus className={mobile ? "w-5 h-5" : "w-3.5 h-3.5"} />
            </HeaderIconButton>
          </>
        }
        pills={
          <>
            <Pill active={pill === "recent"} onClick={() => setPill("recent")}>
              {t("workspace_filter_recent")}
            </Pill>
            <Pill active={pill === "archived"} onClick={() => setPill("archived")}>
              {t("workspace_filter_archived")}
            </Pill>
          </>
        }
        list={
          items.length === 0 ? (
            <EmptyList />
          ) : (
            items.map((item) => (
              <button
                key={item._id}
                onClick={() => setSelectedId(item._id === selectedId ? null : item._id)}
                className={cn(
                  "w-full text-start px-2.5 py-[11px] rounded-xl transition-colors flex items-center gap-2",
                  item._id === selectedId
                    ? "bg-amber-50/70 border border-amber-200/80"
                    : "border border-transparent hover:bg-muted/40"
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] leading-snug text-foreground/85 line-clamp-2">
                    {isFav(item) && (
                      <Star className="w-3 h-3 inline me-1 -mt-0.5 fill-amber-400 text-amber-400" />
                    )}
                    {item.content}
                  </p>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-amber-700/80 truncate">
                      {item.source?.activityTitle ?? t("workspace_source_captured")}
                      {" · "}
                      {relTime(item.createdOn, i18n.language)}
                    </span>
                    <SpaceMiniChips item={item} spaces={spaces} />
                    <ProjectChip item={item} projectById={projectById} spaces={spaces} />
                  </div>
                </div>
                {mobile && <RowChevron />}
              </button>
            ))
          )
        }
        detail={
          selected && (
            <div className="max-w-[640px]">
              <div className="bg-card border border-amber-200/70 rounded-[14px] px-[18px] py-4 mb-3.5">
                <p className="text-[15px] leading-relaxed text-foreground/90 whitespace-pre-wrap">
                  {selected.content}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <ProjectLink item={selected} projectById={projectById} onOpen={(p) => navigate(projectPath(p))} />
                {selected.source?.activityTitle && (
                  <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-primary/5 border border-primary/20 text-primary">
                    {t("workspace_from_source", { name: selected.source.activityTitle })}
                  </span>
                )}
                {selected.category && <CategoryBadge category={selected.category} />}
                {spacesOf(selected, spaces).map((s) => (
                  <SpaceBadge key={s._id} space={s} />
                ))}
                <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-card border border-border/70 text-muted-foreground">
                  {relTime(selected.createdOn, i18n.language)}
                </span>
              </div>
              <UnderneathChips entityId={selected._id} byId={underneath} className="-mt-2 mb-4" />
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setToActionFor(selected)}
                  disabled={busy}
                  className={btnP}
                >
                  {t("convert_to_action", "Convert to Action")}
                </button>
                <button
                  onClick={() => {
                    setEditItem(selected);
                    setAddOpen(true);
                  }}
                  className={btnS}
                >
                  <PenLine className="w-3.5 h-3.5" />
                  {t("edit")}
                </button>
                <button
                  onClick={() => toggleFavorite(selected)}
                  disabled={busy}
                  className={btnS}
                  title={isFav(selected) ? t("remove_from_favorites") : t("add_to_favorites")}
                >
                  <Star
                    className={cn(
                      "w-3.5 h-3.5",
                      isFav(selected) && "fill-amber-400 text-amber-400"
                    )}
                  />
                  {isFav(selected) ? t("remove_from_favorites") : t("add_to_favorites")}
                </button>
                <button
                  onClick={() => setArchived(selected, selected.status !== "archived")}
                  disabled={busy}
                  className={btnS}
                >
                  {selected.status === "archived"
                    ? t("workspace_unarchive")
                    : t("workspace_archive")}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={btnS} aria-label={t("more", "More")}>
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Tag className="h-4 w-4 me-2" />
                        {t("assign_category")}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem onClick={() => setCategory(selected, null)}>
                            <X className="h-4 w-4 me-2 text-muted-foreground" />
                            {t("no_category")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {insightCategories.map((category) => (
                            <DropdownMenuItem
                              key={category._id}
                              onClick={() => setCategory(selected, category._id)}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full border"
                                  style={{ backgroundColor: category.color }}
                                />
                                {category.name}
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    {spaces && spaces.length > 0 && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <Globe className="h-4 w-4 me-2" />
                          {t("assign_to_space", "Assign to Space")}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuSubContent>
                            {spaces.map((space: any) => {
                              const isAssigned = selected.spaceIds?.includes(space._id);
                              return (
                                <DropdownMenuItem
                                  key={space._id}
                                  onClick={() => {
                                    const currentIds = selected.spaceIds || [];
                                    setSpaces(
                                      selected,
                                      isAssigned
                                        ? currentIds.filter((id) => id !== space._id)
                                        : [...currentIds, space._id]
                                    );
                                  }}
                                >
                                  <div className="flex items-center gap-2 w-full">
                                    <div
                                      className="w-3 h-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: space.color }}
                                    />
                                    <span className="flex-1 truncate">{space.name}</span>
                                    {isAssigned && (
                                      <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                    )}
                                  </div>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                      </DropdownMenuSub>
                    )}
                    <MmLiftMenuItem entityId={selected._id} />
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => removeItem(selected)}
                    >
                      <Trash2 className="h-4 w-4 me-2" />
                      {t("delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )
        }
      />

      <InsightDialog
        open={addOpen}
        onOpenChange={(o) => {
          setAddOpen(o);
          if (!o) setEditItem(null);
        }}
        onSave={async (data) => {
          if (editItem) {
            await editTakeaway(retryFetch, {
              id: editItem._id,
              content: data.content,
              category: data.category ?? null,
              spaceIds: data.spaceIds,
            });
            toast.success(t("insight_updated", "Insight updated"));
          } else {
            const response = await createTakeaway(retryFetch, {
              type: "insight",
              content: data.content,
              category: data.category || undefined,
              spaceIds: data.spaceIds,
              source: { type: "user_created" },
            });
            if (response.status !== "CREATED")
              throw new Error(response.fault ?? "Could not save");
            toast.success(t("insight_created"));
          }
          invalidateWorkspace();
        }}
        categories={insightCategories}
        item={editItem ?? undefined}
      />
      <InsightToActionDialog
        open={!!toActionFor}
        onOpenChange={(o) => !o && setToActionFor(null)}
        insight={toActionFor}
        categories={categories.filter((c) => c.type === "action" || c.type === "both")}
        onCreateActions={createActionsFromInsight}
      />
      <AIExtractorDialog
        open={extractOpen}
        onOpenChange={setExtractOpen}
        onExtractComplete={handleExtractComplete}
        categories={categories}
      />
    </>
  );
};

// ── Reminders ────────────────────────────────────────────────────────────────

const RemindersFocus = (props: FocusProps) => {
  const { initialId, onSwitchToClassic, mobile } = props;
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const { resumeProcess } = useProcess();
  const btnP = cn(primaryBtnCls, mobile && "w-full justify-center py-2.5");
  const btnS = cn(secondaryBtnCls, mobile && "flex-1 justify-center py-2.5");
  const [pill, setPill] = useState<"pending" | "sent">("pending");
  const [busy, setBusy] = useState(false);

  const { data, isFetched } = useQuery({
    queryKey: ["workspace", "focus", "reminders", pill],
    queryFn: () =>
      getMyReminders(retryFetch, { status: pill, includeArchived: false }).catch(() => ({
        success: false,
        count: 0,
        reminders: [] as CheckpointReminder[],
      })),
    staleTime: 30_000,
  });

  const items = data?.reminders ?? [];
  const { selected, selectedId, setSelectedId } = useSelection(items, isFetched, initialId);

  const cancel = async (r: CheckpointReminder) => {
    if (busy) return;
    setBusy(true);
    try {
      await cancelReminder(retryFetch, r._id);
      toast.success(t("workspace_reminder_cancelled"));
      setSelectedId(null);
      invalidateWorkspace();
    } catch (e: any) {
      toast.error(e.message ?? "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  const fmtWhen = (r: CheckpointReminder) =>
    new Date(r.scheduledDateTime).toLocaleString(i18n.language, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    });

  return (
    <FocusShell
      title={t("upcoming_reminders")}
      focus={props}
      onCloseDetail={() => setSelectedId(null)}
      headerActions={
        <HeaderIconButton mobile={mobile} onClick={onSwitchToClassic} label={t("workspace_view_full")}>
          <SlidersHorizontal className="w-3.5 h-3.5" />
        </HeaderIconButton>
      }
      pills={
        <>
          <Pill active={pill === "pending"} onClick={() => setPill("pending")}>
            {t("workspace_filter_pending")}
          </Pill>
          <Pill active={pill === "sent"} onClick={() => setPill("sent")}>
            {t("workspace_filter_sent")}
          </Pill>
        </>
      }
      list={
        items.length === 0 ? (
          <EmptyList />
        ) : (
          items.map((r) => (
            <button
              key={r._id}
              onClick={() => setSelectedId(r._id === selectedId ? null : r._id)}
              className={cn(
                "w-full text-start px-2.5 py-[11px] rounded-xl transition-colors flex items-center gap-2",
                r._id === selectedId
                  ? "bg-purple-50/70 border border-purple-200/70"
                  : "border border-transparent hover:bg-muted/40"
              )}
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13px] leading-snug text-foreground/85 line-clamp-1">
                  {r.activityTitle}
                </p>
                <p className="text-[11px] text-muted-foreground/70 mt-0.5">{fmtWhen(r)}</p>
              </div>
              {mobile && <RowChevron />}
            </button>
          ))
        )
      }
      detail={
        selected && (
          <div className="max-w-[640px]">
            <h3 className="text-lg font-semibold tracking-tight text-foreground mb-1.5">
              {selected.activityTitle}
            </h3>
            <div className="flex flex-wrap gap-2 mb-[18px]">
              <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-purple-50 border border-purple-200 text-purple-700">
                {fmtWhen(selected)}
              </span>
              <span className="text-[11px] px-2.5 py-[3px] rounded-full bg-card border border-border/70 text-muted-foreground">
                {t(`workspace_freq_${selected.frequency}`, selected.frequency)}
              </span>
            </div>
            <div className="bg-card border border-border/60 rounded-[14px] px-[18px] py-4 text-[13px] leading-relaxed text-muted-foreground mb-3.5">
              {selected.reminderAction === "resume_engagement"
                ? t("workspace_reminder_resume_ctx")
                : t("workspace_reminder_new_ctx")}
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.fiProcess && selected.reminderAction === "resume_engagement" && (
                <button
                  onClick={() => resumeProcess({ processId: selected.fiProcess })}
                  className={btnP}
                >
                  {t("workspace_resume_activity")}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              )}
              {selected.status === "pending" && (
                <button onClick={() => cancel(selected)} disabled={busy} className={btnS}>
                  {t("workspace_cancel_reminder")}
                </button>
              )}
            </div>
          </div>
        )
      }
    />
  );
};

// ── Mental models ────────────────────────────────────────────────────────────

/** Active pill: active + parked ones that are due again (the server's old
 *  default list); Parked pill: parked; retired never listed. */
export const inModelsPill = (o: MmObject, pill: "active" | "parked", now = Date.now()) => {
  const due = o.status === "parked" && !!o.parkedUntil && new Date(o.parkedUntil).getTime() <= now;
  return pill === "active" ? o.status === "active" || due : o.status === "parked";
};

const ModelsFocus = (props: FocusProps) => {
  const { initialId, mobile, mmInitialVisit, onMmInitialVisitConsumed, onOpenStudio } = props;
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const queryClient = useQueryClient();
  const [pill, setPill] = useState<"active" | "parked">("active");
  const { spaces } = useSpaces();
  const [register, setRegister] = useState(getStoredRegister);
  const { tm } = useMmText(register);

  // Every status: the pills filter here, and a parked or retired model stays
  // openable (and its page — e.g. a write-back prompt — stays mounted when a
  // visit parks or retires it).
  const { data, isFetched, isFetchedAfterMount, isFetching } = useQuery({
    queryKey: ["workspace", "focus", "models"],
    queryFn: () =>
      listObjects(retryFetch, { status: "all", sort: "attention" }).catch(() => ({
        status: "",
        objects: [] as MmObject[],
      })),
    staleTime: 60_000,
  });

  const all = useMemo(() => data?.objects ?? [], [data]);
  const items = useMemo(() => all.filter((o) => inModelsPill(o, pill)), [all, pill]);
  // Selection spans every status. A staged id (maybe brand new) is only
  // dropped against a list fetched after this view mounted.
  const selectionReady = isFetched && !isFetching && (!initialId || isFetchedAfterMount);
  const { selected, selectedId, setSelectedId } = useSelection(all, selectionReady, initialId);
  // The staged visit belongs to the staged model only, and is used once
  const [initialVisit, setInitialVisit] = useState(mmInitialVisit);
  const takeInitialVisit = () => {
    setInitialVisit(undefined);
    onMmInitialVisitConsumed?.();
  };
  useEffect(() => {
    // Show the selected model's pill (retired ones belong to neither)
    if (!selected || inModelsPill(selected, pill)) return;
    if (inModelsPill(selected, "active")) setPill("active");
    else if (inModelsPill(selected, "parked")) setPill("parked");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const select = (id: string | null) => {
    setInitialVisit(undefined);
    setSelectedId(id);
  };

  const changeRegister = (r: typeof register) => {
    setRegister(r);
    storeRegister(r);
  };

  return (
    <FocusShell
      title={t("mm_studio_title", "Mental Models")}
      focus={props}
      onCloseDetail={() => select(null)}
      headerActions={
        onOpenStudio && (
          <button
            type="button"
            onClick={onOpenStudio}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border/70 px-3 text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              mobile ? "h-9" : "h-[26px]"
            )}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {tm("skills_title", "Your skills & patterns")}
          </button>
        )
      }
      pills={
        <>
          <Pill active={pill === "active"} onClick={() => setPill("active")}>
            {t("workspace_filter_active")}
          </Pill>
          <Pill active={pill === "parked"} onClick={() => setPill("parked")}>
            {t("workspace_filter_parked")}
          </Pill>
        </>
      }
      list={
        items.length === 0 ? (
          <EmptyList />
        ) : (
          <MmListSections
            objects={items}
            spaces={spaces}
            tm={tm}
            onOpen={(m) => select(m._id)}
            renderRow={(m) => (
              <MmRow
                object={m}
                selected={m._id === selectedId}
                onClick={() => select(m._id === selectedId ? null : m._id)}
                trailing={mobile ? <RowChevron /> : undefined}
              />
            )}
          />
        )
      }
      detail={
        selected && (
          <MmModelPage
            key={selected._id}
            object={selected}
            register={register}
            onRegisterChange={changeRegister}
            initialVisit={selected._id === initialId ? initialVisit : undefined}
            onInitialVisitUsed={takeInitialVisit}
            onOpenModel={(id) => {
              if (all.some((m) => m._id === id)) select(id);
            }}
            onChanged={() => queryClient.invalidateQueries({ queryKey: ["workspace", "focus", "models"] })}
          />
        )
      }
    />
  );
};

export default WorkspaceFocus;
